import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { BasicMoveListTableItem, IMoveEvent } from 'shared/api/api';
import { getLocationStringNameBySectionId, getPenNameByPenId } from 'shared/helpers/location-helper';
import { localized } from 'shared/state/i18n/i18n';
import { WebAppState } from 'web/state/store.web';
import { FlexColumn } from '../../basic-flex/flex-column';
import '../list-setup.scss';

import { memoizeHashmapLocation } from 'shared/helpers/memoize-getters/memoize-getters';
import SkioldTableGrid from '../../skiold-components/skiold-table/skiold-table-grid/skiold-table-grid';
import { SowListConstants } from '../../stem-animal/animal-lists/table-constants';
import { useBasicMoveList } from './basic-move-list-overview';
import SkioldStableSectionPicker from '../../location/location-picker/skiold-stable-section-picker';
import SkioldFormPenPicker from '../../location/location-picker/skiold-form-pen-picker';
import { checkSectionWorkLists } from 'shared/helpers/work-list-helpers/move-to-helpers/moving-lists-helper';

export const BasicMoveListTable: FC = React.memo(() => {
	const {
		fromPenId,
		fromSectionId,
		setSelected,
		setFilteredData,
		skioldRef,
		toSectionId,
		toPenId,
		setAnimalsInPens,
		animalsInPens,
	} = useBasicMoveList();

	const animals = useSelector((state: WebAppState) => state.stemAnimals.entities);

	const moveEvents = useSelector((state: WebAppState) => state.moveEvents.entities);

	const hashmapLocations = useSelector((state: WebAppState) =>
		memoizeHashmapLocation(state.locations.buildings, state.locations.sections, state.locations.pens),
	);

	useEffect(() => {
		const mappedMoveEvents = moveEvents.keyValuePairSingle2('stemAnimalId');
		const listItems: BasicMoveListTableItem[] = [];
		animals.forEach(animal => {
			if (!animal.animalNumber && !animal.idNumber) {
				return;
			}
			let penId = animal.entrancePenId;
			let entranceToPenDate = animal.entranceDate;
			const moveEvent: IMoveEvent = mappedMoveEvents[animal.id!];

			if (moveEvent && moveEvent.penId) {
				penId = moveEvent.penId;
				entranceToPenDate = moveEvent.moveDate;
			}
			if (!penId || penId !== fromPenId) {
				return;
			}
			listItems.push({
				stemAnimalId: animal.id!,
				animalNumber: animal.animalNumber,
				fromPenId: penId,
				fromSection: getLocationStringNameBySectionId(fromSectionId, hashmapLocations),
				fromSectionId: fromSectionId,
				fromPen: getPenNameByPenId(fromPenId, hashmapLocations),
				toSectionId: toSectionId,
				toPenId: toPenId,
				toSection: getLocationStringNameBySectionId(toSectionId, hashmapLocations),
				toPen: getPenNameByPenId(toPenId, hashmapLocations),
				idNumber: animal.idNumber,
			} as BasicMoveListTableItem);
		});
		setAnimalsInPens(listItems);
	}, [animals, moveEvents, fromPenId, fromSectionId, hashmapLocations, toSectionId, toPenId, setAnimalsInPens]);

	const handleSingleSectionChanged = useCallback(
		(selectedSectionId: string, item: BasicMoveListTableItem) => {
			item.toSectionId = selectedSectionId;
			const { penId, usesPens } = checkSectionWorkLists(selectedSectionId, undefined);

			if (usesPens) {
				item.toPenId = '';
			} else {
				item.toPenId = penId;
			}
			item.toSection = getLocationStringNameBySectionId(selectedSectionId, hashmapLocations);
			item.toPen = getPenNameByPenId(item.toPenId!, hashmapLocations);
			setAnimalsInPens(prev => {
				const newSelected = prev.map(d => (d.animalNumber === item.animalNumber ? item : d));
				return [...newSelected];
			});
		},
		[hashmapLocations, setAnimalsInPens],
	);

	const handleSinglePenChanged = useCallback(
		(selectedPenId: string, item: BasicMoveListTableItem) => {
			item.toPenId = selectedPenId;
			item.toPen = getPenNameByPenId(selectedPenId, hashmapLocations);
			setAnimalsInPens(prev => {
				const newSelected = prev.map(d => (d.animalNumber === item.animalNumber ? item : d));
				return [...newSelected];
			});
		},
		[hashmapLocations, setAnimalsInPens],
	);

	const columns = useMemo(() => {
		return [
			{
				name: 'animalNumber',
				title: localized('animalNumber'),
			},
			{
				name: 'idNumber',
				title: localized('idNumber'),
			},
			{
				name: 'fromSection',
				title: localized('FromSection'),
			},
			{
				name: 'fromPen',
				title: localized('FromPen'),
			},
			{
				name: 'toSection',
				title: localized('ToSection'),
				getCellValue: (d: BasicMoveListTableItem) => (
					<SkioldStableSectionPicker
						theme={'dark'}
						onStableSectionSelected={handleSingleSectionChanged}
						usedInsideTable={true}
						itemFromParent={d}
						sectionId={d.toSectionId}
						isPigProduction={true}
						dropdownIdentifier={'move-list-section-picker'}
					/>
				),
			},
			{
				name: 'toPen',
				title: localized('ToPen'),
				getCellValue: (d: BasicMoveListTableItem) => (
					<SkioldFormPenPicker
						theme={'dark'}
						onPenSelected={handleSinglePenChanged}
						usedInsideTable={true}
						itemFromParent={d}
						sectionId={d.toSectionId!}
						penId={d.toPenId}
						isPigProduction={true}
						dropdownIdentifier={'move-list-pen-picker'}
					/>
				),
			},
		];
	}, []);
	const columnExt = useMemo(() => {
		return [
			{
				columnName: 'animalNumber',
				width: SowListConstants.animalNrWidth,
			},
			{
				columnName: 'idNumber',
				width: SowListConstants.iDNumberWidth,
			},
			{
				columnName: 'fromSection',
				width: SowListConstants.locationWidth,
			},
			{
				columnName: 'fromPen',
				width: SowListConstants.penWidth,
			},
			{
				columnName: 'toSection',
				width: SowListConstants.locationWidth,
			},
			{
				columnName: 'toPen',
				width: SowListConstants.penWidth,
			},
		];
	}, []);

	return (
		<FlexColumn className="work-list">
			<SkioldTableGrid
				ref={skioldRef}
				tableKey={'basicMoveListTable1'}
				columns={columns}
				ColumnExtensions={columnExt}
				data={animalsInPens}
				onFiltersChanged={setFilteredData}
				useSelection={true}
				useSelectAll={true}
				selectedChanged={setSelected}
				selectionHeader={localized('completed')}
			/>
		</FlexColumn>
	);
});
